import styled from "styled-components";
import MainGridLayout from "./MainGridLayout";
import { LayoutDashboardIcon } from "lucide-react";
import CaseLeadChart from "../components/widgets/CaseLeadChart";
import CaseTypesPerUserWidget from "../components/widgets/CaseTypesPerUserWidget";
import CaseDataChart from "../components/widgets/CaseDataChart";
import DataCollectedWidget from "../components/widgets/DataCollectedWidget";
import EvidenceSummaryWidget from "../components/widgets/EvidenceSummaryWidget";
import CasesSummaryWidget from "../components/widgets/CasesSummaryWidget";
import TimeTrackedWidget from "../components/widgets/TimeTrackedWidget";
import TopClientsWidget from "../components/widgets/TopClientsWidget";
import TimeTrackedPerUserWidget from "../components/widgets/TimeTrackedPerUserWidget";
import { useState } from "react";
import { DropDownItem, DropDownMenu } from "@monolith-forensics/monolith-ui";

// Layout configuration interface
interface LayoutConfig {
  w: number;
  h: number;
  x: number;
  y: number;
  i: string;
  // Smallest size at which the widget stays readable. Always sourced from the
  // definitions below, never from a saved layout, so the floor can be raised later.
  minW: number;
  minH: number;
  visible: boolean;
  moved: boolean;
  static: boolean;
}

// Widget item interface
interface WidgetItem {
  title?: string;
  label: string;
  value: string;
  visible: boolean;
  layout: LayoutConfig;
  widget: React.ReactNode;
  description: string;
}

// Saved layout from localStorage
interface SavedLayoutItem {
  layout?: Partial<LayoutConfig>;
}

// Props for styled component
interface AdminViewProps {
  className?: string;
}

// Layout change event type
type LayoutChangeEvent = Partial<LayoutConfig>[];

const persistKey = "overview:layout:admin";

/**
 * Reads the saved layout, tolerating anything that is not the array we wrote.
 *
 * This runs in a `useState` initializer, so an unguarded `JSON.parse` of a corrupt
 * value (or a `.find` on a non array) throws during render and takes the whole
 * admin dashboard down on every visit. `onLayoutChange` is the only writer of this
 * key, so nothing can repair it once that happens. Falling back to defaults turns
 * a permanent crash into a self heal on the next layout change.
 */
const readSavedLayout = (): SavedLayoutItem[] => {
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(persistKey) || "null"
    );
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const items: WidgetItem[] = [
  {
    title: "Case Lead Assignments",
    label: "Case Lead Assignments",
    value: "case_lead_chart",
    visible: true,
    layout: {
      w: 4,
      h: 10,
      x: 0,
      y: 5,
      minW: 3,
      minH: 4,
      visible: true,
      i: "case_lead_chart",
      moved: false,
      static: false,
    },
    widget: <CaseLeadChart />,
    description:
      "Shows the composition of cases by lead and excludes closed cases.",
  },
  {
    title: "Cases Grouped by User and Status",
    label: "Cases Grouped by User and Status",
    value: "case_types_by_user_chart",
    visible: true,
    layout: {
      w: 8,
      h: 10,
      x: 4,
      y: 5,
      minW: 4,
      minH: 5,
      visible: true,
      i: "case_types_by_user_chart",
      moved: false,
      static: false,
    },
    widget: <CaseTypesPerUserWidget />,
    description:
      "Shows the composition of cases by lead and excludes closed cases.",
  },
  {
    title: "Cases & Evidence (last 12 months)",
    label: "Cases & Evidence (last 12 months)",
    value: "case_data_chart",
    visible: true,
    layout: {
      w: 8,
      h: 7,
      x: 0,
      y: 15,
      minW: 4,
      minH: 4,
      visible: true,
      i: "case_data_chart",
      moved: false,
      static: false,
    },
    widget: <CaseDataChart />,
    description:
      "Shows the previous 12 months worth of new cases, evidence, and acquisitions.",
  },
  {
    label: "Data Collected (This Month)",
    layout: {
      w: 3,
      h: 5,
      x: 0,
      y: 0,
      minW: 2,
      minH: 4,
      visible: true,
      i: "data_collected_chart",
      moved: false,
      static: false,
    },
    value: "data_collected_chart",
    visible: true,
    widget: <DataCollectedWidget />,
    description: "The amount of data collected this month from all cases.",
  },
  {
    label: "Evidence Summary (This Month)",
    value: "evidence_summary",
    visible: true,
    layout: {
      w: 3,
      h: 5,
      x: 9,
      y: 0,
      minW: 2,
      minH: 4,
      visible: true,
      i: "evidence_summary",
      moved: false,
      static: false,
    },
    widget: <EvidenceSummaryWidget />,
    description: "The amount of evidence collected this month from all cases.",
  },
  {
    label: "Case Summary (This Month)",
    value: "cases_summary",
    visible: true,
    layout: {
      w: 3,
      h: 5,
      x: 6,
      y: 0,
      minW: 2,
      minH: 4,
      visible: true,
      i: "cases_summary",
      moved: false,
      static: false,
    },
    widget: <CasesSummaryWidget />,
    description: "The amount of evidence collected this month from all cases.",
  },
  {
    label: "Time Tracked (This Month)",
    value: "time_tracked",
    visible: true,
    layout: {
      w: 3,
      h: 5,
      x: 3,
      y: 0,
      minW: 2,
      minH: 4,
      visible: true,
      i: "time_tracked",
      moved: false,
      static: false,
    },
    widget: <TimeTrackedWidget />,
    description: "The amount of evidence collected this month from all cases.",
  },
  {
    title: "Top Clients (Last 6 Months)",
    label: "Top Clients (Last 6 Months)",
    value: "top_clients",
    visible: true,
    layout: {
      w: 4,
      h: 7,
      x: 8,
      y: 15,
      minW: 3,
      minH: 4,
      visible: true,
      i: "top_clients",
      moved: false,
      static: false,
    },
    widget: <TopClientsWidget />,
    description: "The amount of evidence collected this month from all cases.",
  },
  {
    label: "Time Tracked Per User",
    value: "time_tracked_per_user",
    visible: true,
    layout: {
      w: 12,
      h: 12,
      x: 0,
      y: 22,
      minW: 6,
      minH: 6,
      visible: true,
      i: "time_tracked_per_user",
      moved: false,
      static: false,
    },
    widget: <TimeTrackedPerUserWidget />,
    description: "The amount of evidence collected this month from all cases.",
  },
];

const AdminView = styled(({ className }: AdminViewProps) => {
  const [widgetState, setWidgetState] = useState<WidgetItem[]>(() => {
    const savedLayout = readSavedLayout();

    return items.map((layoutItem) => {
      const layout = layoutItem.layout;
      const saved =
        savedLayout?.find((l) => l?.layout?.i === layout.i)?.layout || {};

      const merged = { ...layout, ...saved };

      return {
        ...layoutItem,
        layout: {
          ...merged,
          // Constraints come from the definitions above, never from storage, so a
          // layout saved before these existed (or before a floor was raised) is
          // corrected on load rather than restored at an unreadable size.
          minW: layout.minW,
          minH: layout.minH,
          w: Math.max(merged.w ?? layout.w, layout.minW),
          h: Math.max(merged.h ?? layout.h, layout.minH),
        },
      };
    });
  });

  const onLayoutChange = (layout: LayoutChangeEvent) => {
    setWidgetState((prev) => {
      const newState = prev.map((l) => {
        const newLayout = layout.find((nl) => nl.i === l.layout.i);
        return {
          ...l,
          layout: {
            ...l.layout,
            ...newLayout,
          },
        };
      });

      // Persist layout only. Serializing the whole item array also serialized the
      // React elements in `widget`, which bloated storage and stored nothing usable.
      // setItem can throw (quota, storage disabled), and this runs inside the state
      // updater, so an unguarded throw would abandon the drag the user just made.
      try {
        window.localStorage.setItem(
          persistKey,
          JSON.stringify(newState.map((l) => ({ layout: l.layout })))
        );
      } catch {
        // Layout persistence is a convenience. Losing it must not lose the move.
      }

      return newState;
    });
  };

  const handleSelect = (data: DropDownItem[]) => {
    const selected = data.map((d) => d.value);

    setWidgetState((prev) => {
      return prev.map((l) => {
        return {
          ...l,
          layout: {
            ...l.layout,
            visible: selected.includes(l.layout.i),
          },
        };
      });
    });
  };

  const customizeLayoutMenuItems: DropDownItem[] = items.map((item) => ({
    label: item.label,
    value: item.layout.i,
    data: {
      ...item,
      description: item.description,
      layout: item.layout,
    },
  }));

  const currentlyVisibleItems = customizeLayoutMenuItems.filter(
    (item) =>
      widgetState.find((w) => w.layout.i === item.value)?.layout?.visible !==
      false
  );

  return (
    <div className={className}>
      <div className="menu-bar">
        <DropDownMenu
          data={customizeLayoutMenuItems}
          defaultValue={currentlyVisibleItems}
          multiselect={true}
          onChange={handleSelect}
          arrow={true}
          size="xxs"
          variant="outlined"
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <LayoutDashboardIcon size={14} style={{ marginRight: 5 }} />
            Customize
          </div>
        </DropDownMenu>
      </div>
      <MainGridLayout
        layout={widgetState.map((l) => l.layout)}
        widgets={widgetState.filter((l) => l?.layout?.visible !== false)}
        onLayoutChange={onLayoutChange}
      />
    </div>
  );
})`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  overflow-y: auto;

  .menu-bar {
    display: flex;
    flex-direction: row;
    margin: 10px 13px;
    margin-bottom: 0;
  }

  .react-grid-item.react-grid-placeholder {
    background: none !important;
    border: 3px solid rgb(71, 130, 218);
    transition-duration: 100ms;
    z-index: 2;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    -o-user-select: none;
    user-select: none;
  }
`;

export default AdminView;
